import { Badge, Button, Card, CardBody, CardHeader, FormField, Select, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BIN_MAX_COLUMNS,
  BIN_MAX_LEVELS,
  generateBinCodes,
  type ShelfCreate,
  type WarehouseAreaCreate,
  type WarehouseMapArea,
  type WarehouseMapShelf,
} from '@warehouse/shared';
import { Boxes, Minus, Plus, Squircle, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { warehouseApi, warehouseKeys } from '../api';
import { BinDrawer } from '../components/BinDrawer';
import { MapCanvas, type MapSelection } from '../components/MapCanvas';
import { MAX_ZOOM, MIN_ZOOM } from '../map-layout';
import { useAuthStore, userHasPermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * The warehouse map builder (spec §5.15).
 *
 * Left: the 2D grid, drag-to-move. Right: an inspector for whatever is
 * selected. Creating a shelf shows a LIVE preview of the bin codes it will
 * generate, computed with the same `generateBinCodes` the server uses — the
 * builder should never have to guess whether `direction: reversed` does what
 * they think it does.
 */
export function MapPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const canEdit = userHasPermission(user, permissions, ['warehouse.map.manage']);
  const { confirm, dialog } = useConfirm();

  const [zoom, setZoom] = useState(1);
  const [selection, setSelection] = useState<MapSelection>(null);
  const [creating, setCreating] = useState<'area' | 'shelf' | null>(null);

  const { data, isPending } = useQuery({
    queryKey: warehouseKeys.map(),
    queryFn: () => warehouseApi.map(),
  });
  useLiveQuery(['warehouse'], warehouseKeys.map());

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
  };

  const moveArea = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) =>
      warehouseApi.updateArea(id, { x, y }),
    onSuccess: invalidate,
    onError: (error) => {
      toastError(error);
      invalidate();
    },
  });

  const moveShelf = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) =>
      warehouseApi.updateShelf(id, { x, y }),
    onSuccess: invalidate,
    onError: (error) => {
      toastError(error);
      invalidate();
    },
  });

  const selectedArea = useMemo(
    () =>
      selection?.kind === 'area'
        ? (data?.areas.find((a) => a.id === selection.id) ?? null)
        : null,
    [selection, data],
  );
  const selectedShelf = useMemo(() => {
    if (selection?.kind !== 'shelf') return null;
    for (const area of data?.areas ?? []) {
      const shelf = area.shelves.find((s) => s.id === selection.id);
      if (shelf) return { shelf, area };
    }
    return null;
  }, [selection, data]);

  return (
    <>
      {dialog}
      <PageHeader
        title={t('warehouseCore:map.title')}
        subtitle={t('warehouseCore:map.subtitle')}
        actions={
          canEdit ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                icon={<Squircle />}
                onClick={() => {
                  setSelection(null);
                  setCreating('area');
                }}
              >
                {t('warehouseCore:map.newArea')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={<Boxes />}
                disabled={(data?.areas.length ?? 0) === 0}
                onClick={() => {
                  setCreating('shelf');
                }}
              >
                {t('warehouseCore:map.newShelf')}
              </Button>
            </>
          ) : null
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              icon={<Minus />}
              aria-label="zoom out"
              onClick={() => {
                setZoom((z) => Math.max(MIN_ZOOM, Number((z - 0.2).toFixed(2))));
              }}
            />
            <span className="w-12 text-center font-mono text-xs text-ink-muted">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              icon={<Plus />}
              aria-label="zoom in"
              onClick={() => {
                setZoom((z) => Math.min(MAX_ZOOM, Number((z + 0.2).toFixed(2))));
              }}
            />
            {canEdit ? <p className="text-xs text-ink-muted">{t('warehouseCore:map.gridHint')}</p> : null}
          </div>

          {isPending || !data ? (
            <Surface className="h-96 animate-pulse rounded-2xl" />
          ) : (
            <MapCanvas
              map={data}
              zoom={zoom}
              selection={selection}
              onSelect={(next) => {
                setSelection(next);
                setCreating(null);
              }}
              editable={canEdit}
              onMoveArea={(id, x, y) => {
                moveArea.mutate({ id, x, y });
              }}
              onMoveShelf={(id, x, y) => {
                moveShelf.mutate({ id, x, y });
              }}
            />
          )}

          <Legend />
        </div>

        <aside className="flex flex-col gap-4">
          {creating === 'area' ? (
            <AreaForm
              onCancel={() => {
                setCreating(null);
              }}
              onCreated={() => {
                setCreating(null);
                invalidate();
              }}
            />
          ) : null}

          {creating === 'shelf' && data ? (
            <ShelfForm
              areas={data.areas}
              defaultAreaId={selectedArea?.id ?? data.areas[0]?.id ?? ''}
              onCancel={() => {
                setCreating(null);
              }}
              onCreated={() => {
                setCreating(null);
                invalidate();
              }}
            />
          ) : null}

          {!creating && selectedArea ? (
            <AreaInspector
              area={selectedArea}
              canEdit={canEdit}
              onDelete={() => {
                void (async () => {
                  const ok = await confirm({
                    title: t('warehouseCore:map.deleteArea'),
                    message: t('warehouseCore:map.deleteAreaConfirm', { code: selectedArea.code }),
                    confirmLabel: t('common:actions.delete'),
                    variant: 'danger',
                  });
                  if (!ok) return;
                  try {
                    await warehouseApi.deleteArea(selectedArea.id);
                    toastSuccess(t('warehouseCore:map.areaDeleted'));
                    setSelection(null);
                    invalidate();
                  } catch (error) {
                    toastError(error);
                  }
                })();
              }}
            />
          ) : null}

          {!creating && selectedShelf ? (
            <ShelfInspector
              shelf={selectedShelf.shelf}
              areaCode={selectedShelf.area.code}
              canEdit={canEdit}
              onSaved={invalidate}
              onDelete={() => {
                void (async () => {
                  const ok = await confirm({
                    title: t('warehouseCore:map.deleteShelf'),
                    message: t('warehouseCore:map.deleteShelfConfirm', {
                      code: selectedShelf.shelf.code,
                    }),
                    confirmLabel: t('common:actions.delete'),
                    variant: 'danger',
                  });
                  if (!ok) return;
                  try {
                    await warehouseApi.deleteShelf(selectedShelf.shelf.id);
                    toastSuccess(t('warehouseCore:map.shelfDeleted'));
                    setSelection(null);
                    invalidate();
                  } catch (error) {
                    toastError(error);
                  }
                })();
              }}
            />
          ) : null}

          {!creating && !selectedArea && !selectedShelf && selection?.kind !== 'bin' ? (
            <Surface variant="subtle" className="p-4 text-sm text-ink-muted">
              {(data?.areas.length ?? 0) === 0
                ? t('warehouseCore:map.noAreasHelp')
                : t('warehouseCore:map.gridHint')}
            </Surface>
          ) : null}
        </aside>
      </div>

      <BinDrawer
        binId={selection?.kind === 'bin' ? selection.id : null}
        onClose={() => {
          setSelection(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------

function Legend(): ReactNode {
  const { t } = useTranslation('warehouseCore');
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted">
      <span className="font-mono uppercase tracking-[0.14em]">{t('map.legend')}</span>
      <LegendSwatch className="border-brand-500/50 bg-brand-500/5" label={t('map.storage')} />
      <LegendSwatch className="border-sky-400/60 bg-sky-400/10" label={t('map.floor')} />
      <LegendSwatch className="border-ink/20 bg-surface-raised" label={t('map.shelf')} />
      <LegendSwatch className="border-ink/40 bg-ink/20" label={t('map.pillar')} />
    </div>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block size-3 rounded border ${className}`} aria-hidden />
      {label}
    </span>
  );
}

function AreaForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [form, setForm] = useState<WarehouseAreaCreate>({
    code: '',
    name: '',
    kind: 'storage',
    floorRole: null,
    x: 0,
    y: 0,
    w: 6,
    h: 4,
    sort: 0,
  });

  const create = useMutation({
    mutationFn: () => warehouseApi.createArea({ ...form, code: form.code.toUpperCase() }),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:map.areaCreated'));
      onCreated();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <Card>
      <CardHeader>
        <h2 className="font-display text-base font-semibold text-ink">{t('warehouseCore:map.newArea')}</h2>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <FormField
          label={t('warehouseCore:map.areaCode')}
          hint={t('warehouseCore:map.areaCodeHint')}
          value={form.code}
          maxLength={8}
          onChange={(e) => {
            setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }));
          }}
        />
        <FormField
          label={t('warehouseCore:map.areaName')}
          value={form.name}
          onChange={(e) => {
            setForm((f) => ({ ...f, name: e.target.value }));
          }}
        />
        <Select
          label={t('warehouseCore:map.areaKind')}
          value={form.kind}
          options={[
            { value: 'storage', label: t('warehouseCore:map.storage') },
            { value: 'floor', label: t('warehouseCore:map.floor') },
          ]}
          onChange={(e) => {
            const kind = e.target.value as 'storage' | 'floor';
            setForm((f) => ({ ...f, kind, floorRole: kind === 'floor' ? 'staging' : null }));
          }}
        />
        {form.kind === 'floor' ? (
          <Select
            label={t('warehouseCore:map.floorRole')}
            value={form.floorRole ?? 'staging'}
            options={(['receiving', 'packing', 'dispatch', 'production', 'staging'] as const).map((role) => ({
              value: role,
              label: t(`warehouseCore:map.floorRoles.${role}`),
            }))}
            onChange={(e) => {
              setForm((f) => ({ ...f, floorRole: e.target.value as WarehouseAreaCreate['floorRole'] }));
            }}
          />
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={t('warehouseCore:map.size') + ' — B'}
            value={form.w}
            min={1}
            onChange={(w) => {
              setForm((f) => ({ ...f, w }));
            }}
          />
          <NumberField
            label={t('warehouseCore:map.size') + ' — H'}
            value={form.h}
            min={1}
            onChange={(h) => {
              setForm((f) => ({ ...f, h }));
            }}
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            loading={create.isPending}
            disabled={form.code.length === 0 || form.name.length === 0}
            onClick={() => {
              create.mutate();
            }}
          >
            {t('common:actions.create')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ShelfForm({
  areas,
  defaultAreaId,
  onCancel,
  onCreated,
}: {
  areas: WarehouseMapArea[];
  defaultAreaId: string;
  onCancel: () => void;
  onCreated: () => void;
}): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [form, setForm] = useState<ShelfCreate>({
    areaId: defaultAreaId,
    code: '',
    direction: 'normal',
    x: 1,
    y: 1,
    w: 4,
    h: 1,
    orientation: 'h',
    columns: 4,
    levels: 3,
  });

  const area = areas.find((a) => a.id === form.areaId);
  const preview = useMemo(
    () =>
      area && form.code
        ? generateBinCodes({
            areaCode: area.code,
            shelfCode: form.code.toUpperCase(),
            columns: form.columns,
            levels: form.levels,
            direction: form.direction,
          })
        : [],
    [area, form.code, form.columns, form.levels, form.direction],
  );

  const create = useMutation({
    mutationFn: () =>
      warehouseApi.createShelf({ ...form, code: form.code.toUpperCase(), w: form.columns }),
    onSuccess: (result) => {
      toastSuccess(t('warehouseCore:map.binsCreated', { count: result.binsCreated }));
      onCreated();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <Card>
      <CardHeader>
        <h2 className="font-display text-base font-semibold text-ink">{t('warehouseCore:map.newShelf')}</h2>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <Select
          label={t('warehouseCore:common.selectArea')}
          value={form.areaId}
          options={areas.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))}
          onChange={(e) => {
            setForm((f) => ({ ...f, areaId: e.target.value }));
          }}
        />
        <FormField
          label={t('warehouseCore:map.shelfCode')}
          hint={t('warehouseCore:map.shelfCodeHint')}
          value={form.code}
          maxLength={8}
          onChange={(e) => {
            setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }));
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={t('warehouseCore:map.columns')}
            value={form.columns}
            min={1}
            max={BIN_MAX_COLUMNS}
            onChange={(columns) => {
              setForm((f) => ({ ...f, columns }));
            }}
          />
          <NumberField
            label={t('warehouseCore:map.levels')}
            value={form.levels}
            min={1}
            max={BIN_MAX_LEVELS}
            onChange={(levels) => {
              setForm((f) => ({ ...f, levels }));
            }}
          />
        </div>
        <Select
          label={t('warehouseCore:map.direction')}
          hint={t('warehouseCore:map.directionHint')}
          value={form.direction}
          options={[
            { value: 'normal', label: t('warehouseCore:map.directions.normal') },
            { value: 'reversed', label: t('warehouseCore:map.directions.reversed') },
          ]}
          onChange={(e) => {
            setForm((f) => ({ ...f, direction: e.target.value as ShelfCreate['direction'] }));
          }}
        />

        <BinCodePreview codes={preview.map((p) => p.code)} />

        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            loading={create.isPending}
            disabled={form.code.length === 0 || !form.areaId}
            onClick={() => {
              create.mutate();
            }}
          >
            {t('common:actions.create')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export function BinCodePreview({ codes }: { codes: string[] }): ReactNode {
  const { t } = useTranslation('warehouseCore');
  if (codes.length === 0) return null;
  const shown = codes.slice(0, 12);
  return (
    <div data-testid="bin-code-preview">
      <p className="mb-1.5 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
        {t('map.preview')} · {t('map.previewCount', { count: codes.length })}
      </p>
      <div className="flex flex-wrap gap-1">
        {shown.map((code) => (
          <span key={code} className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-[11px] text-ink-light">
            {code}
          </span>
        ))}
        {codes.length > shown.length ? (
          <span className="px-1 py-0.5 text-[11px] text-ink-muted">
            {t('map.previewMore', { count: codes.length - shown.length })}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function AreaInspector({
  area,
  canEdit,
  onDelete,
}: {
  area: WarehouseMapArea;
  canEdit: boolean;
  onDelete: () => void;
}): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <h2 className="font-display text-base font-semibold text-ink">
            {area.code} · {area.name}
          </h2>
          <Badge tone="neutral">
            {area.kind === 'floor'
              ? t(`warehouseCore:map.floorRoles.${area.floorRole ?? 'staging'}`)
              : t('warehouseCore:map.storage')}
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-3 text-sm">
        <Row label={t('warehouseCore:map.position')} value={`${area.x}, ${area.y}`} />
        <Row label={t('warehouseCore:map.size')} value={`${area.w} × ${area.h}`} />
        <Row label={t('warehouseCore:bins.title')} value={String(area.binCount)} />
        <Row label={t('warehouseCore:map.shelf')} value={String(area.shelves.length)} />
        {canEdit ? (
          <Button variant="danger" size="sm" icon={<Trash2 />} onClick={onDelete}>
            {t('warehouseCore:map.deleteArea')}
          </Button>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ShelfInspector({
  shelf,
  areaCode,
  canEdit,
  onSaved,
  onDelete,
}: {
  shelf: WarehouseMapShelf;
  areaCode: string;
  canEdit: boolean;
  onSaved: () => void;
  onDelete: () => void;
}): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [columns, setColumns] = useState(shelf.columns);
  const [levels, setLevels] = useState(shelf.levels);
  const [direction, setDirection] = useState(shelf.direction);
  const dirty = columns !== shelf.columns || levels !== shelf.levels || direction !== shelf.direction;

  const preview = useMemo(
    () => generateBinCodes({ areaCode, shelfCode: shelf.code, columns, levels, direction }),
    [areaCode, shelf.code, columns, levels, direction],
  );

  const save = useMutation({
    mutationFn: () => warehouseApi.updateShelf(shelf.id, { columns, levels, direction, w: columns }),
    onSuccess: (result) => {
      toastSuccess(t('warehouseCore:map.shelfSaved', result));
      onSaved();
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <Card>
      <CardHeader>
        <h2 className="font-display text-base font-semibold text-ink">
          {t('warehouseCore:map.shelf')} {areaCode}-{shelf.code}
        </h2>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        <Row
          label={t('warehouseCore:bins.title')}
          value={`${shelf.occupiedBinCount}/${shelf.binCount}`}
        />
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={t('warehouseCore:map.columns')}
            value={columns}
            min={1}
            max={BIN_MAX_COLUMNS}
            disabled={!canEdit}
            onChange={setColumns}
          />
          <NumberField
            label={t('warehouseCore:map.levels')}
            value={levels}
            min={1}
            max={BIN_MAX_LEVELS}
            disabled={!canEdit}
            onChange={setLevels}
          />
        </div>
        <Select
          label={t('warehouseCore:map.direction')}
          value={direction}
          disabled={!canEdit}
          options={[
            { value: 'normal', label: t('warehouseCore:map.directions.normal') },
            { value: 'reversed', label: t('warehouseCore:map.directions.reversed') },
          ]}
          onChange={(e) => {
            setDirection(e.target.value as typeof direction);
          }}
        />

        <BinCodePreview codes={preview.map((p) => p.code)} />

        {canEdit ? (
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => {
                save.mutate();
              }}
            >
              {t('common:actions.save')}
            </Button>
            <Button variant="danger" size="sm" icon={<Trash2 />} onClick={onDelete}>
              {t('common:actions.delete')}
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</span>
      <span className="text-sm text-ink">{value}</span>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}): ReactNode {
  return (
    <FormField
      label={label}
      type="number"
      inputMode="numeric"
      value={String(value)}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => {
        const next = Number(e.target.value);
        if (Number.isFinite(next)) onChange(next);
      }}
    />
  );
}
